import { Field, Formik } from "formik";
import React, { useEffect } from "react";
import { Button, Col, Form, Input, Label, Modal, Row } from "reactstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { globalAPI } from "./global";
import { queryKeys } from "./queryKeys";
import { loadingToast, successToast, tog_backdrop } from "./common";
import horizontalLogo from "./Media/popupLogo.png";
import {
  balanceRatingClass,
  formatBalance,
  formatEnergy,
  formatEuro,
  formatNumber,
  formatPercent,
} from "./fueleu";
import {
  FuelEUDailyResult,
  FuelEUPeriodRow,
  formatDays,
  formatRequirement,
  horizonStatus,
} from "./fueleuDaily";
import "./App.css";

interface FuelEUDailyResultsModalProps {
  state: boolean;
  setState: any;
  id: number;
  result: FuelEUDailyResult | null;
  vesselName: string;
  year: number;
}

const LEAD_FIELDS = [
  { name: "name", label: "Full name" },
  { name: "designation", label: "Designation" },
  { name: "company_name", label: "Company name" },
  { name: "email", label: "E-mail" },
  { name: "mobile", label: "Mobile" },
];

/**
 * The daily plan's results screen.
 *
 * The annual modal's twin structurally — verdict left, lead capture right — but
 * it answers a different question, so it leads with the requirement rather than
 * with the balance. An operator reading this already expects a deficit; what
 * they came for is the tonnage and the date.
 */
const FuelEUDailyResultsModal = ({
  state,
  setState,
  id,
  result,
  vesselName,
  year,
}: FuelEUDailyResultsModalProps) => {
  useEffect(() => {
    toast.dismiss();
  }, []);

  const summary = result?.summary;

  return (
    <Modal
      isOpen={state}
      toggle={() => tog_backdrop(setState)}
      backdrop="static"
      id="fueleuModal"
      size="xl"
      className="mt-5"
      scrollable
    >
      <div className="modal-body img-bg">
        <Row className="mb-0">
          <Col>
            <button
              type="button"
              className="btn-close float-right"
              onClick={() => setState(false)}
              aria-label="Close"
            />
          </Col>
        </Row>

        <Row className="g-4">
          <Col lg={7} className="result-pane">
            <Row className="mb-4 align-items-center">
              <Col xs="auto">
                <img src={horizontalLogo} alt="" height="45" />
              </Col>
              <Col>
                <h3 className="modal-title">FuelEU Daily Plan</h3>
              </Col>
            </Row>

            {result && summary ? (
              <>
                <h2 className="fueleu-verdict-heading">
                  Over {formatDays(summary.days)} of {year},{" "}
                  {vesselName}&rsquo;s balance is a
                </h2>
                <div
                  className={`CII-rating-${balanceRatingClass(
                    summary.status
                  )} fueleu-verdict`}
                >
                  <span className="fueleu-verdict__word">{summary.status}</span>
                  <span className="fueleu-verdict__figure">
                    {formatBalance(summary.compliance_balance)} tCO₂eq
                  </span>
                </div>

                {/* The headline the plan exists to produce, stated as a
                    sentence before it is stated as a figure. A tonnage on its
                    own does not say what it buys. */}
                {summary.elapsed_days > 0 && (
                  /* Stated before the requirement, because it is what makes the
                     requirement what it is: the first stretch is already spent,
                     and everything after it pays for both halves. */
                  <p className="fueleu-optimiser-lead">
                    {formatDays(summary.elapsed_days)} of this period were
                    already burnt, closing at{" "}
                    <strong>
                      {formatBalance(summary.opening_balance)} tCO₂eq
                    </strong>
                    . That is fixed — the{" "}
                    <strong>{formatDays(summary.remaining_days)}</strong> that
                    remain have to carry it as well as their own.
                  </p>
                )}

                {summary.required_alternative_mass !== null ? (
                  <p className="fueleu-optimiser-lead">
                    Bunkering{" "}
                    <strong>
                      {formatRequirement(summary.required_alternative_mass)}
                    </strong>{" "}
                    of{" "}
                    <strong>{result.alternative_fuel ?? "the biofuel"}</strong>{" "}
                    over the remaining {formatDays(summary.remaining_days)} — an
                    average of{" "}
                    <strong>
                      {formatNumber(summary.alternative_mass_per_day, 2)} mt a
                      day
                    </strong>{" "}
                    — in place of{" "}
                    <strong>
                      {formatNumber(summary.displaced_fossil_mass, 2)} mt
                    </strong>{" "}
                    of <strong>{result.displaced_fuel ?? "fossil fuel"}</strong>{" "}
                    holds the balance for the whole period at zero.
                    {result.displaced_reason && (
                      <span className="fueleu-optimiser-note">
                        {" "}
                        That fuel is chosen because it is {result.displaced_reason}.
                      </span>
                    )}
                  </p>
                ) : (
                  <p className="fueleu-optimiser-lead fueleu-optimiser-lead--none">
                    {summary.requirement_reason ??
                      "Select a biofuel to see what the plan would take."}
                  </p>
                )}

                <dl className="fueleu-figures fueleu-figures--blurred">
                  <Figure
                    label="Attained GHG intensity"
                    value={`${formatNumber(
                      summary.attained_intensity,
                      3
                    )} gCO₂eq/MJ`}
                  />
                  <Figure
                    label={`${year} target`}
                    value={`${formatNumber(
                      summary.target_intensity,
                      3
                    )} gCO₂eq/MJ`}
                    note={`${formatNumber(
                      result.reference_intensity,
                      2
                    )} less ${formatPercent(result.reduction, 1)}`}
                  />
                  <Figure
                    label="Energy over the plan"
                    value={formatEnergy(summary.total_energy)}
                  />
                  <Figure
                    label="Annex IV penalty"
                    value={formatEuro(summary.penalty_eur)}
                    note={
                      summary.is_compliant
                        ? "No penalty on a surplus"
                        : `${formatEuro(summary.penalty_per_day)} a day`
                    }
                  />
                  <Figure
                    label="Biofuel required"
                    value={formatRequirement(summary.required_alternative_mass)}
                    note={
                      summary.alternative_mass_per_day === null
                        ? result.alternative_fuel ?? undefined
                        : `${formatNumber(
                            summary.alternative_mass_per_day,
                            2
                          )} mt/day over ${formatDays(summary.remaining_days)}`
                    }
                  />
                  {summary.elapsed_days > 0 && (
                    <Figure
                      label="Opening balance"
                      value={`${formatBalance(summary.opening_balance)} tCO₂eq`}
                      note={`Already banked after ${formatDays(
                        summary.elapsed_days
                      )}`}
                    />
                  )}
                  <Figure
                    label="First day in deficit"
                    /* Null is "it never goes negative inside this plan", which
                       is the good news and must not render as day 0. */
                    value={
                      summary.first_deficit_day === null
                        ? "Never"
                        : `Day ${summary.first_deficit_day}`
                    }
                    note={
                      summary.first_deficit_day === null
                        ? "The balance stays positive throughout"
                        : "The balance goes negative and stays there"
                    }
                  />
                </dl>

                {/* ---------------------------------------------- the curve */}
                {result.checkpoints.length > 1 && (
                  <>
                    <h5 className="fueleu-section">
                      Requirement as the plan runs
                    </h5>
                    <div className="fueleu-table-wrap">
                      <table className="fueleu-table">
                        <thead>
                          <tr>
                            <th>Day</th>
                            <th>Energy</th>
                            <th>Balance</th>
                            <th>Penalty if unfixed</th>
                            {/* Named for what the figure is for, not just what
                                it is: beside a Status of "Deficit" a bare
                                "required" reads as a contradiction, when the
                                two columns are in fact the position and the
                                remedy for it. */}
                            <th>
                              {result.alternative_fuel ?? "Biofuel"} to break
                              even
                            </th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.checkpoints.map((entry, index) => {
                            const status = horizonStatus(
                              entry.status,
                              entry.required_alternative_mass,
                              index === result.checkpoints.length - 1
                            );
                            return (
                            <tr key={entry.day}>
                              <td>{entry.day}</td>
                              <td>{formatEnergy(entry.total_energy)}</td>
                              <td>
                                {formatBalance(entry.compliance_balance)} tCO₂eq
                              </td>
                              <td>{formatEuro(entry.penalty_eur)}</td>
                              <td>
                                {formatRequirement(
                                  entry.required_alternative_mass
                                )}
                              </td>
                              <td>
                                {/* The balance in this row is still the negative
                                    one, so the pill says which reading it is. */}
                                <span
                                  className={`fueleu-pill fueleu-pill--${balanceRatingClass(
                                    status
                                  )}`}
                                  title={
                                    status === entry.status
                                      ? undefined
                                      : `With the ${
                                          result.alternative_fuel ?? "biofuel"
                                        } in the column beside it, the plan ends at break-even.`
                                  }
                                >
                                  {status}
                                </span>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {/* --------------------------------------- the blend ladder */}
                {result.sensitivity.length > 0 && (
                  <>
                    <h5 className="fueleu-section">Blend sensitivity</h5>
                    <p className="fueleu-hint">
                      What each blend ratio does to the balance for the whole
                      period. The share is a fraction of the period&rsquo;s total
                      energy &mdash; that is what the balance is scored on
                      &mdash; while the fuel itself goes into the{" "}
                      {formatDays(summary.remaining_days)} that are left, so the
                      ladder stops where those days run out of{" "}
                      {result.displaced_fuel ?? "fossil fuel"} to displace.
                    </p>
                    <div className="fueleu-table-wrap">
                      <table className="fueleu-table">
                        <thead>
                          <tr>
                            <th>Share of energy</th>
                            <th>{result.alternative_fuel ?? "Alternative"}</th>
                            <th>
                              {result.displaced_fuel ?? "Fossil fuel"} displaced
                            </th>
                            <th>GHG intensity</th>
                            <th>Compliance balance</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.sensitivity.map((row, index) => (
                            <tr
                              key={index}
                              /* The row where the balance actually crosses zero
                                 is inserted by the API rather than landed on by
                                 the ladder, so it is worth pointing at: it is
                                 the requirement, restated as a blend ratio. */
                              className={
                                row.status === "Break-even"
                                  ? "fueleu-table__breakeven"
                                  : undefined
                              }
                            >
                              <td>
                                <Blurred>
                                  {formatPercent(row.energy_share, 3)}
                                </Blurred>
                              </td>
                              <td>
                                <Blurred>
                                  {row.alt_mass === null
                                    ? "—"
                                    : `${formatNumber(row.alt_mass, 2)} mt`}
                                </Blurred>
                              </td>
                              <td>
                                <Blurred>
                                  {row.displaced_fossil_mass === null
                                    ? "—"
                                    : `${formatNumber(
                                        row.displaced_fossil_mass,
                                        2
                                      )} mt`}
                                </Blurred>
                              </td>
                              <td>
                                <Blurred>
                                  {formatNumber(row.intensity, 3)}
                                </Blurred>
                              </td>
                              <td>
                                <Blurred>
                                  {formatBalance(row.compliance_balance)} tCO₂eq
                                </Blurred>
                              </td>
                              <td>
                                <Blurred>
                                  <span
                                    className={`fueleu-pill fueleu-pill--${balanceRatingClass(
                                      row.status
                                    )}`}
                                  >
                                    {row.status}
                                  </span>
                                </Blurred>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {/* -------------------------------------------- the phases */}
                <PeriodTable
                  title="Already consumed against what remains"
                  rows={result.phases}
                  fuel={result.alternative_fuel}
                />

                {/* ------------------------------------------ the quarters */}
                {/* The quarters run to the same horizon as the checkpoint
                    table above, so the last row carries the same status. Only
                    the running total is kept: the per-quarter requirement is a
                    different question from the one this table answers. */}
                <PeriodTable
                  title="Quarter by quarter"
                  rows={result.quarters}
                  fuel={result.alternative_fuel}
                  requirement="to-date"
                  showDayRange
                  lastRowIsHorizon
                />

                {/* ------------------------------------- the fuel breakdown */}
                <h5 className="fueleu-section">
                  Fuel burnt over {formatDays(summary.days)}
                </h5>
                <div className="fueleu-table-wrap">
                  <table className="fueleu-table">
                    <thead>
                      <tr>
                        <th>Fuel</th>
                        <th>Total</th>
                        <th>Per day</th>
                        <th>Intensity</th>
                        <th>Energy share</th>
                        <th>Emissions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.fuels.map((fuel, index) => (
                        <tr key={index}>
                          <td>{fuel.name ?? "—"}</td>
                          <td>
                            {formatNumber(fuel.mass, 2)} mt
                            {fuel.reward_multiplier > 1 && (
                              <em className="fueleu-tag">
                                RFNBO ×{fuel.reward_multiplier}
                              </em>
                            )}
                          </td>
                          <td>
                            {formatNumber(
                              (fuel.mass ?? 0) / (summary.days || 1),
                              2
                            )}{" "}
                            mt
                          </td>
                          <td>{formatNumber(fuel.intensity, 3)}</td>
                          <td>{formatPercent(fuel.energy_share)}</td>
                          <td>{formatNumber(fuel.emissions / 1e6, 2)} tCO₂eq</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="fueleu-footnote">
                  A planning figure, not a verifier-approved FuelEU report.
                  Planned consumption is taken as a constant daily rate over the
                  days it covers, and fuel already burnt is spread evenly across
                  the stretch it was entered for; the balance is accrued on that
                  basis, and the formal compliance assessment is made on the
                  reported calendar year. Emission
                  factors are the published Annex I/II defaults; where a fuel has
                  no default, the certified pathway value you entered was used.
                  The biofuel requirement assumes the biofuel displaces fossil
                  energy one for one rather than adding to it. Ice-class
                  adjustment, wind-assistance reward, pooling and banking are not
                  modelled.
                </p>
              </>
            ) : (
              <p>No result to show.</p>
            )}
          </Col>

          <Col lg={5} className="lead-pane">
            <Formik
              initialValues={{
                name: "",
                company_name: "",
                email: "",
                mobile: null,
                i_agree: false,
                designation: "",
              }}
              validationSchema={Yup.object({
                name: Yup.string().required("Field is required!"),
                company_name: Yup.string().required("Field is required!"),
                email: Yup.string()
                  .email("Invalid e-mail")
                  .required("Field is required!"),
                mobile: Yup.string()
                  .matches(/^\d{10}$/, "Please enter 10 digits")
                  .required("Field is required!"),
              })}
              onSubmit={(values: any, actions: any) => {
                actions.setSubmitting(false);
                loadingToast(
                  "Please wait as we prepare to send you this report via email"
                );
                globalAPI
                  .patch(
                    `${queryKeys.FuelEUDailyCalculator.url()}${id}/`,
                    values
                  )
                  .then((res) => {
                    if (res.status === 200) {
                      successToast(
                        "You will receive the report via email within a few minutes."
                      );
                      setState(false);
                    }
                  })
                  .catch((err) => {
                    console.log(err);
                  });
              }}
            >
              {(props) => (
                <Form onSubmit={props.handleSubmit} className="lead-form">
                  <h4 className="font-white text-center p-3">
                    Get your FuelEU daily plan report by email
                  </h4>
                  <p
                    className="mb-3 font-white text-center"
                    style={{ fontSize: "12px" }}
                  >
                    Please note that your email will not be spammed, and your
                    personal or professional information will not be shared with
                    third parties.
                  </p>
                  {LEAD_FIELDS.map(({ name, label }) => (
                    <div className="mb-2" key={name}>
                      <div className="form-floating">
                        <Field
                          type="text"
                          name={name}
                          id={`daily_${name}`}
                          placeholder={label}
                          className="form-control"
                        />
                        <Label
                          htmlFor={`daily_${name}`}
                          className="d-flex align-items-center asteric"
                        >
                          {label}
                        </Label>
                      </div>
                      {(props.errors as any)[name] && (
                        <Label className="error">
                          {(props.errors as any)[name]}
                        </Label>
                      )}
                    </div>
                  ))}
                  <div className="ele_row mt-3 mb-4">
                    <Input
                      type="checkbox"
                      name="i_agree"
                      id="daily_i_agree"
                      onChange={props.handleChange}
                    />
                    <Label htmlFor="daily_i_agree" className="mb-0 ms-2 font-white">
                      I would like to be contacted through SMS or WhatsApp from
                      Ecosail about my ship&rsquo;s FuelEU compliance
                    </Label>
                  </div>
                  <Button
                    type="submit"
                    style={{ backgroundColor: "#20a8a4" }}
                    className="btn logo-blue font-white w-100"
                  >
                    Send my details
                  </Button>
                </Form>
              )}
            </Formik>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

/**
 * One period table — the quarters, or the halves.
 *
 * Both views are shown per row, because they answer different questions: the
 * cumulative columns are the regulatory position (FuelEU scores the year, not
 * the quarter), and the period columns are what a quarterly review is about.
 * Labelling them apart is what stops the two being read as one.
 */
const PeriodTable = ({
  title,
  rows,
  fuel,
  requirement = "both",
  showDayRange = false,
  lastRowIsHorizon = false,
}: {
  title: string;
  rows: FuelEUPeriodRow[];
  fuel: string | null;
  /**
   * Which biofuel columns to carry. "to-date" drops the per-period one, for a
   * table where the running total is the only figure being read.
   */
  requirement?: "both" | "to-date" | "none";
  /**
   * State the day each row runs to, under its day count, on every row rather
   * than only the one the horizon cut short. "To date" means a different span
   * in each row, and this is where that span is named.
   */
  showDayRange?: boolean;
  /**
   * Whether the final row ends on the plan's horizon, and so carries the status
   * its requirement reaches rather than the balance it currently has. See
   * `horizonStatus`.
   */
  lastRowIsHorizon?: boolean;
}) => {
  if (!rows || rows.length === 0) return null;

  return (
    <>
      <h5 className="fueleu-section">{title}</h5>
      <div className="fueleu-table-wrap">
        <table className="fueleu-table">
          <thead>
            <tr>
              <th>Period</th>
              <th>Days</th>
              <th>Balance in period</th>
              <th>Balance to date</th>
              {requirement === "both" && (
                <th>{fuel ?? "Biofuel"} in period</th>
              )}
              {requirement !== "none" && (
                <th>{fuel ?? "Biofuel"} to date</th>
              )}
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const status = horizonStatus(
                row.status,
                row.cumulative_alternative_mass,
                lastRowIsHorizon && index === rows.length - 1
              );
              return (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td>
                  {row.days}
                  {/* Where the row states its own span, that span also says
                      which periods the horizon cut short — so the tag below
                      would be saying it twice. */}
                  {showDayRange ? (
                    <div className="fueleu-daterange">
                      (up to day {row.end_day})
                    </div>
                  ) : (
                    /* A period short of its calendar length is short because the
                       horizon cut it, not because the calendar did. A reader
                       comparing Q3 against Q2 needs to know which. */
                    row.truncated && (
                      <em className="fueleu-tag">to day {row.end_day}</em>
                    )
                  )}
                </td>
                <td>{formatBalance(row.period_balance)} tCO₂eq</td>
                <td>{formatBalance(row.cumulative_balance)} tCO₂eq</td>
                {requirement === "both" && (
                  <td>{formatRequirement(row.period_alternative_mass)}</td>
                )}
                {/* A dash on its own does not say whether the period needs
                    nothing or can be given nothing, and those are opposite
                    news. The reason rides along as a tooltip rather than a
                    column, which would be a sentence in a table of numbers. */}
                {requirement !== "none" && (
                  <td title={row.requirement_reason ?? undefined}>
                    {formatRequirement(row.cumulative_alternative_mass)}
                  </td>
                )}
                <td>
                  <span
                    className={`fueleu-pill fueleu-pill--${balanceRatingClass(
                      status
                    )}`}
                  >
                    {status}
                  </span>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

/**
 * Frosts over a value in the blend sensitivity ladder.
 *
 * The ladder is the part worth asking for — the whole curve of blends and the
 * ratio where the balance crosses zero — so it is shown as a shape without its
 * numbers, and the lead form on the right is what clears it. The headline
 * figures blur too, by CSS on their own <dl>. Cosmetic, not a lock: the API
 * sends these rows regardless and they sit in the DOM.
 */
const Blurred = ({ children }: { children: React.ReactNode }) => (
  <span className="fueleu-blurred">{children}</span>
);

const Figure = ({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) => (
  <div className="fueleu-figure">
    <dt>{label}</dt>
    <dd>
      {value}
      {note && <em>{note}</em>}
    </dd>
  </div>
);

export default FuelEUDailyResultsModal;
